/**
 * ComposedPinchViewport — COMPOSED 본문(PagedComposerView 낱장 묶음)을 모바일 핀치/팬 viewer로 감싸는
 * **앱 층** 래퍼.
 *
 * 규약:
 *   1) fit scale은 **정적 CSS transform**으로 먼저 적용(뷰포트 폭에 페이지 폭을 맞춤),
 *   2) 그 위 "추가 줌"(scale 1 = fit 상태, 1~3)과 pan만 `PinchViewerFrame`(react-zoom-pan-pinch)이 관장.
 *   이중 scale을 피하려면 이 순서를 지켜야 한다("라이브러리 scale 1 = 이 fit 상태").
 *
 * 왜 앱 층인가: rc(`@safetyhub/report-composer`)의 `PagedComposerView`는 낱장을 **고정 px 폭**
 * (`pagePx(page).w`, A4=794)으로 렌더하고 스케일 개념이 없다. rc에 손대지 않고(신규 Tailwind 유틸이
 * 앱 CSS에 미생성되는 함정 회피) 앱에서 감싸는 것으로 충분하다.
 *
 * 측정 안전성: rc의 낱장 분할(`measureBody`)은 **offsetHeight**를 쓴다 — CSS transform의 영향을 받지
 * 않으므로 이 래퍼의 scale이 페이지 분할을 왜곡하지 않는다.
 *
 * 데스크탑(`useDeviceLayout() === 'desktop'`)은 viewer(핀치/팬)를 적용하지 않고 정적 렌더로
 * 떨어진다 — 단 **축소만 하는 fit**(`ComposedFitWidthFrame`)은 건다. 가로(landscape) 낱장(1123px)이
 * 상세 본문 폭(≈832px)보다 넓어 `overflow-x-auto`만으로는 왼쪽이 스크롤로도 닿지 않던 결함
 * (2026-09-14 위험성평가 결과서 실측). 세로 낱장은 scale 1이라 종전과 같다.
 */
import type { ReactNode } from 'react';
import { useDeviceLayout } from '../../../hooks/useDeviceLayout';
import { useFitWidthMeasure } from '../../../hooks/useFitWidthMeasure';
import { PinchViewerFrame } from '../PinchViewerFrame';
import { ComposedFitWidthFrame } from './ComposedFitWidthFrame';

interface ComposedPinchViewportProps {
  /** 낱장 1장의 렌더 폭(px). rc `pagePx(template.page).w` — A4 세로 794, 가로 1123. */
  pageWidthPx: number;
  /** 페이지 수 — 바뀌면 PinchViewerFrame이 viewer 높이를 재측정한다. */
  pageCount: number;
  /** PagedComposerView 낱장 묶음. */
  children: ReactNode;
}

/*
 * fit scale(`calcFitScale`) = **실측한 컨테이너 폭** / 페이지 폭(최대 1) — 정의와 실측 effect는
 * 데스크탑 정적 프레임과 공유하는 `useFitWidthMeasure`에 있다(같은 규약을 두 벌 두지 않는다).
 *
 * ⚠️ legacy `calcFitScale`은 가용 폭을 `window.innerWidth - 32`로 **가정**하는데, 이 viewer가
 * 놓이는 상세 레이아웃은 그 값과 다르다(2026-08-19 dev 실측: 390px 폰에서 가정 358px vs 실제
 * 컨테이너 326px → 낱장이 397px로 그려져 **71px이 잘렸다**). 그래서 가정 대신 실측한다.
 *
 * 하한(legacy 50%)도 두지 않는다 — 하한이 걸리면 컨테이너보다 넓어져 다시 잘린다. 축소가
 * 과하면 사용자가 **핀치로 확대**하면 되고, 그게 이 viewer의 존재 이유다.
 */

export function ComposedPinchViewport({
  pageWidthPx,
  pageCount,
  children,
}: ComposedPinchViewportProps) {
  const layout = useDeviceLayout();
  const enabled = layout !== 'desktop';

  // 가용 폭은 **우리가 소유한 width:100% 노드**(measureRef)에서 잰다 — PinchViewerFrame 내부 구조
  // (rzpp wrapper)를 parentElement로 더듬지 않기 위해서다(그쪽이 바뀌면 조용히 틀어진다).
  // 스케일 컨테이너(wrapper-shrink): transform은 레이아웃 박스를 줄이지 않으므로, 축소된 콘텐츠의
  // **실제 높이**(자연높이 × fitScale)를 바깥 박스에 되돌려준다. 이게 없으면 viewer 안에 콘텐츠
  // 아래로 빈 공간이 남아 그만큼 헛도는 pan이 생긴다.
  // 데스크탑(enabled=false)에선 실측하지 않는다 — 정적 프레임이 제 훅으로 잰다.
  // pageCount: 낱장 배치가 확정되면 자연높이를 다시 잡는다(ResizeObserver 백업).
  const { measureRef, innerRef, fitScale, naturalHeight } = useFitWidthMeasure(
    pageWidthPx,
    enabled,
    pageCount,
  );

  if (!enabled) {
    // 데스크탑: 정적 렌더 + 축소만 하는 fit(가로 낱장이 본문 폭을 넘으면 축소, 세로는 scale 1로
    // 종전과 동일). 핀치/팬 viewer는 없다 — 네이티브 스크롤.
    return <ComposedFitWidthFrame pageWidthPx={pageWidthPx}>{children}</ComposedFitWidthFrame>;
  }

  return (
    <PinchViewerFrame enabled remeasureKey={pageCount}>
      {/* 폭 실측 전용 노드 — width:100%뿐이라 레이아웃 중립이다. */}
      <div ref={measureRef} style={{ width: '100%' }}>
        <div
          style={{
            width: pageWidthPx * fitScale,
            height: naturalHeight != null ? naturalHeight * fitScale : undefined,
            margin: '0 auto',
          }}
        >
          <div
            ref={innerRef}
            style={{
              width: pageWidthPx,
              transform: `scale(${fitScale})`,
              transformOrigin: 'top left',
            }}
          >
            {children}
          </div>
        </div>
      </div>
    </PinchViewerFrame>
  );
}

export default ComposedPinchViewport;
